import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { QRCodeCanvas } from 'qrcode.react';

interface StripeRequirements {
  currentlyDue: string[];
  pendingVerification: string[];
  pastDue: string[];
  eventuallyDue: string[];
  disabledReason?: string;
}

interface StripeStatus {
  accountId?: string;
  payoutsEnabled?: boolean;
  chargesEnabled?: boolean;
  requirements: StripeRequirements;
  lastStatusSyncAt?: string;
  lastOnboardingLinkCreatedAt?: string;
  lastOnboardingCompletedAt?: string;
  lastOnboardingReturnUrl?: string;
  lastOnboardingRefreshUrl?: string;
}

interface PartnerStats {
  referralCode: string;
  balance: number;
  totalReferrals: number;
  stripe?: StripeStatus;
  stripeCountry?: StripeCountry;
}

interface Referral {
  id: string;
  case_id: string;
  commission: number;
  created_at: string;
}

interface PayoutRecord {
  id: string;
  period_label: string;
  amount_cents: number;
  currency: string;
  status: string;
  attempt?: number;
  scheduled_for?: string | null;
  retry_scheduled_for?: string | null;
  stripe_transfer_id?: string | null;
  stripe_balance_transaction_id?: string | null;
  fee_cents?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface StripeCountry {
  code?: string;
  name?: string;
}

interface PartnerPayoutTier {
  periodLabel: string;
  referralCount: number;
  tier: number;
  baseAmountCents: number;
  bonusAmountCents: number;
  totalAmountCents: number;
  bonusPerReferralCents: number;
  nextTierReferralCount?: number | null;
  nextTierReferralsNeeded?: number | null;
  currency: string;
  retroactiveBonusApplied: boolean;
  computedAt?: string | null;
  windowStart?: string | null;
  windowEnd?: string | null;
}

const SUPPORTED_STRIPE_COUNTRIES: { code: string; name: string }[] = [
  { code: 'AT', name: 'Austria' },
  { code: 'AU', name: 'Australia' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'CA', name: 'Canada' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DE', name: 'Germany' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'ES', name: 'Spain' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'GI', name: 'Gibraltar' },
  { code: 'GR', name: 'Greece' },
  { code: 'HK', name: 'Hong Kong SAR China' },
  { code: 'HR', name: 'Croatia' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'LV', name: 'Latvia' },
  { code: 'MT', name: 'Malta' },
  { code: 'MX', name: 'Mexico' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NO', name: 'Norway' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'SE', name: 'Sweden' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'US', name: 'United States' },
].sort((a, b) => a.name.localeCompare(b.name));

const ReferralsDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState<PartnerStats | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [payouts, setPayouts] = useState<PayoutRecord[]>([]);
  const [payoutTier, setPayoutTier] = useState<PartnerPayoutTier | null>(null);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [payoutMethod, setPayoutMethod] = useState<'stripe' | 'paypal'>('paypal');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [countryCode, setCountryCode] = useState('');
  const [countrySaving, setCountrySaving] = useState(false);
  const [dashboardLinkLoading, setDashboardLinkLoading] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);
  const accountLocked = Boolean(stats?.stripe?.payoutsEnabled);

  const formatTimestamp = (value?: string) => {
    if (!value) {
      return 'Not synced yet';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'Not synced yet';
    }
    return date.toLocaleString();
  };

  const normaliseStripeStatus = (stripe?: StripeStatus | null): StripeStatus | undefined => {
    if (!stripe) return undefined;
    return {
      ...stripe,
      requirements: {
        currentlyDue: stripe.requirements?.currentlyDue ?? [],
        pendingVerification: stripe.requirements?.pendingVerification ?? [],
        pastDue: stripe.requirements?.pastDue ?? [],
        eventuallyDue: stripe.requirements?.eventuallyDue ?? [],
        disabledReason: stripe.requirements?.disabledReason,
      },
    };
  };

  const loadStats = async (token: string) => {
    const statsResponse = await fetch(`${process.env.REACT_APP_API_URL}/partner/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!statsResponse.ok) {
      if (statsResponse.status === 401 || statsResponse.status === 403) {
        throw new Error('Access Denied: You are not currently enrolled in the partner program.');
      }
      throw new Error('Failed to fetch partner stats');
    }
    const statsData = await statsResponse.json();
    const stripeCountry = statsData.stripeCountry ?? {};
    const countryFromApi = stripeCountry.code ?? '';
    setCountryCode(countryFromApi);
    setStats({
      referralCode: statsData.referralCode,
      balance: typeof statsData.balance === 'number' ? statsData.balance : Number(statsData.balance ?? 0),
      totalReferrals: Number(statsData.totalReferrals ?? 0),
      stripe: normaliseStripeStatus(statsData.stripe),
      stripeCountry: {
        code: countryFromApi || undefined,
        name: stripeCountry.name ?? undefined,
      },
    });
    setPayoutMethod(statsData.payout_method === 'stripe' ? 'stripe' : 'paypal');
    setPaypalEmail(statsData.paypal_email || '');
  };

  const loadReferrals = async (token: string) => {
    const referralsResponse = await fetch(`${process.env.REACT_APP_API_URL}/partner/referrals`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!referralsResponse.ok) throw new Error('Failed to fetch referrals');
    const referralsData = await referralsResponse.json();
    setReferrals(referralsData.referrals ?? []);
  };

  const loadPayouts = async (token: string) => {
    const payoutsResponse = await fetch(`${process.env.REACT_APP_API_URL}/partner/payouts`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!payoutsResponse.ok) throw new Error('Failed to fetch payout history');
    const payoutsData = await payoutsResponse.json();
    setPayouts(payoutsData.payouts ?? []);
  };

  const loadPayoutTier = async (token: string) => {
    const tierResponse = await fetch(`${process.env.REACT_APP_API_URL}/partner/payout-tier`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!tierResponse.ok) {
      throw new Error('Failed to fetch payout tier details');
    }

    const tierData = await tierResponse.json();
    const toNumber = (value: unknown, fallback = 0): number => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };
    const toOptionalNumber = (value: unknown): number | null => {
      if (value === null || value === undefined) {
        return null;
      }
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    setPayoutTier({
      periodLabel: typeof tierData.periodLabel === 'string' ? tierData.periodLabel : '',
      referralCount: toNumber(tierData.referralCount, 0),
      tier: toNumber(tierData.tier, 0),
      baseAmountCents: toNumber(tierData.baseAmountCents, 0),
      bonusAmountCents: toNumber(tierData.bonusAmountCents, 0),
      totalAmountCents: toNumber(tierData.totalAmountCents, 0),
      bonusPerReferralCents: toNumber(tierData.bonusPerReferralCents, 0),
      nextTierReferralCount: toOptionalNumber(tierData.nextTierReferralCount),
      nextTierReferralsNeeded: toOptionalNumber(tierData.nextTierReferralsNeeded),
      currency: typeof tierData.currency === 'string' && tierData.currency ? tierData.currency : 'EUR',
      retroactiveBonusApplied: Boolean(tierData.retroactiveBonusApplied),
      computedAt: typeof tierData.computedAt === 'string' ? tierData.computedAt : null,
      windowStart: typeof tierData.windowStart === 'string' ? tierData.windowStart : null,
      windowEnd: typeof tierData.windowEnd === 'string' ? tierData.windowEnd : null,
    });
  };

  const refreshStripeStatus = async () => {
    if (!currentUser) return;
    try {
      setSyncing(true);
      const token = await currentUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/partner/stripe/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to refresh Stripe status');
      }

      const refreshed = await response.json();
      const updatedStripe = normaliseStripeStatus(refreshed.stripe);
      setStats(prev => {
        if (prev) {
          return { ...prev, stripe: updatedStripe };
        }
        return {
          referralCode: '',
          balance: 0,
          totalReferrals: 0,
          stripe: updatedStripe,
          stripeCountry: undefined,
        };
      });
      setNotice('Stripe status updated. If payouts still show action required, please review the checklist below.');
      try {
        await loadStats(token);
      } catch (statsErr: any) {
        // Surface the most helpful message we have while leaving the refreshed stripe snapshot in place.
        if (!error) {
          setError(statsErr.message ?? 'Unable to refresh partner stats after updating Stripe.');
        }
      }
    } catch (err: any) {
      setNotice(null);
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleCountryChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (!currentUser) return;
    if (accountLocked) {
      setNotice('Payout country is locked after Stripe onboarding. Contact support if it needs to change.');
      return;
    }
    const newCode = event.target.value;
    if (!newCode) {
      // Prevent clearing the selection; revert to previous value.
      setCountryCode(prev => prev || '');
      setNotice('Select your payout country to enable Stripe onboarding.');
      return;
    }

    if (newCode === countryCode) {
      return;
    }

    const previousCode = countryCode;
    setCountryCode(newCode);
    try {
      setCountrySaving(true);
      setNotice(null);
      const token = await currentUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/partner/stripe/country`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ countryCode: newCode }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.detail || 'Failed to save payout country');
      }

      const payload = await response.json();
      setError('');
      setStats(prev => (prev ? { ...prev, stripeCountry: { code: payload.countryCode, name: payload.countryName } } : prev));
      setNotice(`Payout country saved: ${payload.countryName}. You can now continue with Stripe onboarding.`);
    } catch (err: any) {
      setCountryCode(previousCode);
      setNotice(err.message || 'Failed to save payout country. Please try again.');
    } finally {
      setCountrySaving(false);
    }
  };

  const handleStripeOnboarding = async () => {
    if (!currentUser) return;
    if (!countryCode) {
      setNotice('Select your payout country before connecting to Stripe.');
      return;
    }
    try {
      setLinkLoading(true);
      setError('');
      setNotice(null);
      const token = await currentUser.getIdToken();
      const returnUrl = `${window.location.origin}${window.location.pathname}?stripe=return`;
      const response = await fetch(`${process.env.REACT_APP_API_URL}/partner/stripe/account-link`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          returnUrl,
          refreshUrl: returnUrl,
          collect: 'eventually_due',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate Stripe onboarding link');
      }

      const link = await response.json();
      if (!link?.url) {
        throw new Error('Stripe onboarding link response was invalid');
      }

      setNotice('Opening Stripe to finish onboarding…');
      window.location.href = link.url;
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLinkLoading(false);
    }
  };

  const handleOpenStripeDashboard = async () => {
    if (!currentUser) return;
    if (!stats?.stripe?.accountId) {
      setNotice('Stripe account is not linked yet. Complete onboarding first.');
      return;
    }

    try {
      setDashboardLinkLoading(true);
      setError('');
      setNotice(null);
      const token = await currentUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/partner/stripe/dashboard-link`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || 'Failed to open Stripe dashboard');
      }

      const link = await response.json();
      if (!link?.url) {
        throw new Error('Stripe dashboard link response was invalid');
      }

      window.open(link.url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setNotice(err.message || 'Unable to open Stripe dashboard right now.');
    } finally {
      setDashboardLinkLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const token = await currentUser.getIdToken();
        await Promise.all([loadStats(token), loadReferrals(token), loadPayouts(token), loadPayoutTier(token)]);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'return') {
      setNotice('Thanks for completing the Stripe form. Checking your payout status…');
      refreshStripeStatus();
      params.delete('stripe');
      const search = params.toString();
      const newUrl = `${window.location.pathname}${search ? `?${search}` : ''}`;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [currentUser]);

  const handlePayoutInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    
    if (payoutMethod === 'paypal' && !paypalEmail) {
        alert("Please enter a valid PayPal email.");
        return;
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/partner/payout-info`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
            paypal_email: paypalEmail,
            payout_method: payoutMethod 
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update payout information');
      }

      alert('Payout information updated successfully!');
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const formatCurrency = (amountCents?: number, currencyCode?: string) => {
    if (typeof amountCents !== 'number') {
      return '—';
    }
    const currency = (currencyCode || 'USD').toUpperCase();
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amountCents / 100);
    } catch (err) {
      return `${(amountCents / 100).toFixed(2)} ${currency}`;
    }
  };

  const formatPeriodLabel = (periodLabel?: string) => {
    if (!periodLabel) {
      return 'Current month';
    }

    const [yearPart, monthPart] = periodLabel.split('-');
    const year = Number(yearPart);
    const monthIndex = Number(monthPart) - 1;
    if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
      return periodLabel;
    }

    const date = new Date(Date.UTC(year, monthIndex, 1));
    return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  };

  const formatDateTimeOrDash = (value?: string | null) => {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    return date.toLocaleString();
  };

  const formatStatus = (status?: string) => {
    if (!status) return 'pending';
    return status.replace(/_/g, ' ');
  };

  const downloadQRCode = () => {
    if (qrRef.current) {
      const canvas = qrRef.current.querySelector('canvas');
      if (canvas) {
        const pngUrl = canvas
          .toDataURL('image/png')
          .replace('image/png', 'image/octet-stream');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = 'coepi-referral-qr.png';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    }
  };

  const renderPayoutTierPanel = () => {
    if (!payoutTier) {
      return null;
    }

    const currency = (payoutTier.currency || 'EUR').toUpperCase();
    const periodName = formatPeriodLabel(payoutTier.periodLabel);
    const tierLabel = payoutTier.tier > 0 ? `Tier ${payoutTier.tier}` : 'Base tier';
    const totalFormatted = formatCurrency(payoutTier.totalAmountCents, currency);
    const baseFormatted = formatCurrency(payoutTier.baseAmountCents, currency);
    const bonusFormatted = formatCurrency(payoutTier.bonusAmountCents, currency);
    const bonusPerReferralFormatted = formatCurrency(payoutTier.bonusPerReferralCents, currency);
    const hasNextTier =
      payoutTier.nextTierReferralCount !== null &&
      payoutTier.nextTierReferralCount !== undefined &&
      payoutTier.nextTierReferralsNeeded !== null &&
      payoutTier.nextTierReferralsNeeded !== undefined &&
      payoutTier.nextTierReferralsNeeded > 0;

    const nextTierMessage = hasNextTier
      ? `Refer ${payoutTier.nextTierReferralsNeeded} more referral${payoutTier.nextTierReferralsNeeded === 1 ? '' : 's'} this month to unlock the next tier at ${payoutTier.nextTierReferralCount} total.`
      : 'You are currently on the highest tier available for this programme.';

    const retroCopy = payoutTier.retroactiveBonusApplied
      ? 'Retroactive bonus active: every referral this month already pays the higher tier rate.'
      : 'Bonuses are retroactive: hit the next tier and the higher rate will apply automatically to every referral from this month.';

    return (
      <div style={{ border: '1px solid #ccc', padding: '20px', marginBottom: '20px' }}>
        <h3>Current Month Payout Tier</h3>
        <p style={{ marginBottom: '16px' }}>
          <strong>{tierLabel}</strong>
          {periodName ? ` · ${periodName}` : null}
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px',
            marginBottom: '16px',
          }}
        >
          <div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#555' }}>Referrals tracked</p>
            <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: 600 }}>{payoutTier.referralCount}</p>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#555' }}>Projected payout</p>
            <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600 }}>{totalFormatted}</p>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#555' }}>
              Base {baseFormatted} · Bonus {bonusFormatted}
            </p>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#555' }}>Bonus per referral</p>
            <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600 }}>
              {bonusPerReferralFormatted === '—' ? '—' : `${bonusPerReferralFormatted} / referral`}
            </p>
          </div>
        </div>
        <p style={{ marginBottom: '8px', color: '#333' }}>{nextTierMessage}</p>
        <p style={{ marginBottom: '8px', color: '#333' }}>{retroCopy}</p>
        <p style={{ marginBottom: 0, fontSize: '0.8rem', color: '#666' }}>
          Last updated {formatDateTimeOrDash(payoutTier.computedAt)} · Data refreshes automatically from Firebase aggregates.
        </p>
      </div>
    );
  };

  const renderStripeStatusPanel = () => {
    const stripe = stats?.stripe;
    const countryOptionExists = !countryCode || SUPPORTED_STRIPE_COUNTRIES.some(option => option.code === countryCode);
    const panelAccountLocked = Boolean(stripe?.payoutsEnabled);
    const onboardingDisabled = !countryCode || linkLoading || syncing || countrySaving;
    
    const renderCountrySelector = () => (
      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="stripe-country" style={{ display: 'block', fontWeight: 600, marginBottom: '8px' }}>
          Payout country*
        </label>
        <select
          id="stripe-country"
          value={countryCode}
          onChange={handleCountryChange}
          disabled={countrySaving || linkLoading || panelAccountLocked}
          style={{ width: '100%', maxWidth: '320px', padding: '10px' }}
        >
          <option value="">Select your payout country</option>
          {!countryOptionExists && countryCode && (
            <option value={countryCode}>{stats?.stripeCountry?.name ?? countryCode}</option>
          )}
          {SUPPORTED_STRIPE_COUNTRIES.map(option => (
            <option key={option.code} value={option.code}>
              {option.name}
            </option>
          ))}
        </select>
        {countryCode && (
          <p style={{ fontSize: '0.85rem', color: '#333', marginTop: '6px' }}>
            Current selection: {stats?.stripeCountry?.name ?? SUPPORTED_STRIPE_COUNTRIES.find(option => option.code === countryCode)?.name ?? countryCode} ({countryCode})
          </p>
        )}
        <p style={{ fontSize: '0.85rem', color: '#555', marginTop: '8px' }}>
          Choose the country where your business or tax residence is registered. This determines which Stripe Connect requirements apply.
        </p>
        {countrySaving && (
          <p style={{ fontSize: '0.85rem', color: '#555', marginTop: '4px' }}>Saving payout country…</p>
        )}
        {!countryCode && !countrySaving && !panelAccountLocked && (
          <p style={{ fontSize: '0.85rem', color: '#b85504', marginTop: '4px' }}>
            Select your payout country to unlock Stripe onboarding.
          </p>
        )}
        {panelAccountLocked && (
          <p style={{ fontSize: '0.85rem', color: '#555', marginTop: '4px' }}>
            Stripe payouts are active. Contact support if the payout country needs to change.
          </p>
        )}
      </div>
    );

    if (!stripe) {
      return (
        <div style={{ border: '1px solid #ccc', padding: '20px', marginBottom: '20px' }}>
          <h3>Stripe Payout Status</h3>
          <p>We haven&apos;t detected a Stripe Connect account yet. Start onboarding to enable payouts.</p>
          {renderCountrySelector()}
          <button
            onClick={handleStripeOnboarding}
            disabled={onboardingDisabled}
            style={{ padding: '10px 16px', marginTop: '12px' }}
          >
            {linkLoading ? 'Redirecting…' : 'Connect with Stripe'}
          </button>
        </div>
      );
    }

    const requirements = stripe.requirements || { currentlyDue: [], pendingVerification: [], pastDue: [], eventuallyDue: [], disabledReason: undefined };
    const currentlyDue = requirements.currentlyDue || [];
    const pendingVerification = requirements.pendingVerification || [];
    const readiness = stripe.payoutsEnabled
      ? { label: 'Ready for payouts', description: 'Stripe reports payouts are enabled for this account.' }
      : currentlyDue.length > 0
        ? { label: 'Action required', description: 'Stripe needs additional information before payouts can resume.' }
        : { label: 'Pending verification', description: 'Stripe is reviewing submitted information. You will be notified if further action is required.' };

    return (
      <div style={{ border: '1px solid #ccc', padding: '20px', marginBottom: '20px' }}>
        <h3>Stripe Payout Status</h3>
        {renderCountrySelector()}
        <p style={{ marginBottom: '8px' }}>
          Status: <strong>{readiness.label}</strong>
        </p>
        <p style={{ marginBottom: '16px' }}>{readiness.description}</p>
        {!stripe.payoutsEnabled && currentlyDue.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <strong>Information needed:</strong>
            <ul style={{ marginTop: '8px' }}>
              {currentlyDue.map(item => (
                <li key={item}>{item.replace(/_/g, ' ')}</li>
              ))}
            </ul>
          </div>
        )}
        {!stripe.payoutsEnabled && currentlyDue.length === 0 && pendingVerification.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <strong>Pending verification:</strong>
            <ul style={{ marginTop: '8px' }}>
              {pendingVerification.map(item => (
                <li key={item}>{item.replace(/_/g, ' ')}</li>
              ))}
            </ul>
          </div>
        )}
        <p style={{ fontSize: '0.85rem', color: '#555' }}>
          Last synced: {formatTimestamp(stripe.lastStatusSyncAt)}
        </p>
        {stripe.lastOnboardingLinkCreatedAt && (
          <p style={{ fontSize: '0.8rem', color: '#777' }}>
            Last onboarding attempt: {formatTimestamp(stripe.lastOnboardingLinkCreatedAt)}
          </p>
        )}
        {!stripe.payoutsEnabled && (
          <button
            onClick={handleStripeOnboarding}
            disabled={onboardingDisabled}
            style={{ padding: '10px 16px', marginTop: '16px' }}
          >
            {linkLoading ? 'Redirecting…' : stripe.accountId ? 'Resume Stripe Setup' : 'Connect with Stripe'}
          </button>
        )}
        {stripe.accountId && (
          <button
            onClick={handleOpenStripeDashboard}
            disabled={dashboardLinkLoading}
            style={{ padding: '10px 16px', marginTop: '12px', marginLeft: '0' }}
          >
            {dashboardLinkLoading ? 'Opening Stripe…' : 'Open Stripe dashboard'}
          </button>
        )}
        {stripe.accountId && (
          <p style={{ fontSize: '0.85rem', color: '#555', marginTop: '8px' }}>
            Manage payout bank accounts, tax details, or contact information directly inside the Stripe dashboard.
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div style={{ color: 'red' }}>Error: {error}</div>;
  }

  return (
    <div>
      {notice && (
        <div style={{ background: '#eef5ff', border: '1px solid #aac4ff', padding: '12px', marginBottom: '16px' }}>
          {notice}
        </div>
      )}
      {syncing && !notice && (
        <div style={{ background: '#f7f7f7', border: '1px solid #ddd', padding: '12px', marginBottom: '16px' }}>
          Updating Stripe status…
        </div>
      )}
      <h2>Referrals Dashboard</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '40px' }}>
        <div>
          {renderPayoutTierPanel()}
          <div style={{ border: '1px solid #ccc', padding: '20px', marginBottom: '20px' }}>
            <h3>Payout Method</h3>
            <div style={{ marginBottom: '16px' }}>
                <label style={{ marginRight: '20px' }}>
                    <input
                        type="radio"
                        name="payoutMethod"
                        value="paypal"
                        checked={payoutMethod === 'paypal'}
                        onChange={() => setPayoutMethod('paypal')}
                    /> PayPal (Recommended)
                </label>
                <label>
                    <input
                        type="radio"
                        name="payoutMethod"
                        value="stripe"
                        checked={payoutMethod === 'stripe'}
                        onChange={() => setPayoutMethod('stripe')}
                    /> Stripe Connect
                </label>
            </div>            
            {payoutMethod === 'paypal' ? (
                <div style={{ marginTop: '20px' }}>
                    <p style={{marginBottom: '10px'}}>Enter your PayPal email address to receive payouts.</p>
                    <form onSubmit={handlePayoutInfoSubmit}>
                    <input
                        type="email"
                        value={paypalEmail}
                        onChange={(e) => setPaypalEmail(e.target.value)}
                        placeholder="Enter your PayPal email"
                        style={{ width: '300px', padding: '10px' }}
                        required={payoutMethod === 'paypal'}
                    />
                    <button type="submit" style={{ padding: '10px', marginLeft: '12px' }}>Save PayPal Info</button>
                    </form>
                </div>
            ) : (
                <>
                    {renderStripeStatusPanel()}
                     <div style={{marginTop: '10px'}}>
                        <button onClick={handlePayoutInfoSubmit} style={{ padding: '8px 12px' }}>Save Payout Preference</button>
                     </div>
                </>
            )}
          </div>
          <div style={{ display: 'flex', gap: '20px', margin: '20px 0' }}>
            <div style={{ border: '1px solid #ccc', padding: '20px', flex: 1 }}>
              <h3>Total Referrals</h3>
              <p>{stats?.totalReferrals ?? 0}</p>
            </div>

            <div style={{ border: '1px solid #ccc', padding: '20px', flex: 1 }}>
              <h3>Pending Balance</h3>
              <p>${stats?.balance?.toFixed(2) ?? '0.00'}</p>
            </div>
          </div>

          {/* <div style={{ marginTop: '40px' }}>
            <h3>Payout Information</h3>
            <form onSubmit={handlePayoutInfoSubmit}>
              <input
                type="email"
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                placeholder="Enter your PayPal email"
                style={{ width: '300px', padding: '10px' }}
              />
              <button type="submit" style={{ padding: '10px', marginLeft: '12px' }}>Save</button>
            </form>
          </div> */}

          <div style={{ marginTop: '40px', border: '1px solid #ccc', padding: '20px' }}>
            <h3>Stripe Payouts</h3>
            <p style={{ marginBottom: '10px' }}>
              Monthly referral payouts are processed automatically through your connected Stripe account on the first of the month.
            </p>
            <p style={{ marginBottom: 0 }}>
              Need to update your payout destination? Use the <strong>Open Stripe dashboard</strong> button above to manage bank accounts, tax settings, and personal details directly in Stripe.
            </p>
          </div>

          <div style={{ marginTop: '40px' }}>
            <h3>Referral History</h3>
            {referrals.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #ccc', padding: '10px' }}>Case ID</th>
                    <th style={{ border: '1px solid #ccc', padding: '10px' }}>Commission</th>
                    <th style={{ border: '1px solid #ccc', padding: '10px' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map(referral => (
                    <tr key={referral.id}>
                      <td style={{ border: '1px solid #ccc', padding: '10px' }}>{referral.case_id}</td>
                      <td style={{ border: '1px solid #ccc', padding: '10px' }}>${(referral.commission / 100).toFixed(2)}</td>
                      <td style={{ border: '1px solid #ccc', padding: '10px' }}>{new Date(referral.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No referrals yet.</p>
            )}
          </div>

          <div style={{ marginTop: '40px' }}>
            <h3>Payout History</h3>
            {payouts.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #ccc', padding: '10px' }}>Period</th>
                    <th style={{ border: '1px solid #ccc', padding: '10px' }}>Amount</th>
                    <th style={{ border: '1px solid #ccc', padding: '10px' }}>Status</th>
                    <th style={{ border: '1px solid #ccc', padding: '10px' }}>Stripe Transfer</th>
                    <th style={{ border: '1px solid #ccc', padding: '10px' }}>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((payout) => (
                    <tr key={payout.id}>
                      <td style={{ border: '1px solid #ccc', padding: '10px' }}>{payout.period_label}</td>
                      <td style={{ border: '1px solid #ccc', padding: '10px' }}>{formatCurrency(payout.amount_cents, payout.currency)}</td>
                      <td style={{ border: '1px solid #ccc', padding: '10px' }}>{formatStatus(payout.status)}</td>
                      <td style={{ border: '1px solid #ccc', padding: '10px' }}>{payout.stripe_transfer_id ?? '—'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '10px' }}>{payout.updated_at ? new Date(payout.updated_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No payouts generated yet.</p>
            )}
          </div>
        </div>

        <div>
          <div style={{ marginBottom: '40px' }}>
            <h3>Your Referral Link</h3>
            {stats?.referralCode ? (
              <>
                <input type="text" value={`https://coepi.eu/?ref=${stats.referralCode}`} readOnly style={{ width: '100%', padding: '10px', boxSizing: 'border-box' }} />
                <button onClick={() => navigator.clipboard.writeText(`https://coepi.eu/?ref=${stats.referralCode}`)} style={{ padding: '10px', width: '100%', marginTop: '10px' }}>Copy</button>
              </>
            ) : (
              <p>No referral code has been assigned to your account yet. Please contact support.</p>
            )}
          </div>

          {stats?.referralCode && (
            <div style={{ border: '1px solid #ccc', padding: '20px', textAlign: 'center' }}>
              <h3>QR Code</h3>
              <div ref={qrRef} style={{ margin: '20px 0', display: 'inline-block' }}>
                <QRCodeCanvas value={`https://coepi.eu/?ref=${stats.referralCode}`} size={256} />
              </div>
              <button onClick={downloadQRCode} style={{ padding: '10px', width: '100%' }}>Download QR Code</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralsDashboard;
